import React from 'react';
import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import TaskName from "../../../../components/TaskName";
import { MissingTaker, WaitingReasonIcon } from "../../../../components/WaitingProductsQueue";
import { slotLabel } from "../../../../utils/task";
import { useNumberOfBags } from "../../../../hooks/useNumberOfBags";

export const PRIORITIES = [
  { value: 0, label: "Aucune", title: "passe à son heure de départ" },
  { value: 1, label: "Élevée", title: "avant les commandes sans priorité du même jour" },
  { value: 2, label: "Urgente", title: "avant tout le reste du même jour" },
];

// Interrupteur à trois positions (aucune, élevée, urgente) ; flèches au clavier
export const PriorityToggle = ({ taskId, value, canOrder, busy, onChange }) => {
  const current = PRIORITIES.find(p => p.value === value) || PRIORITIES[0];
  const disabled = !canOrder || busy;
  const choose = (next) => { if (!disabled && next !== value) onChange(taskId, next); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); choose(Math.min(2, (value || 0) + 1)); }
    if (e.key === "ArrowLeft") { e.preventDefault(); choose(Math.max(0, (value || 0) - 1)); }
  };
  return (
    <span
      className={"priority-switch pos-" + (value || 0) + (disabled ? " is-disabled" : "")}
      role="slider"
      aria-label="Priorité"
      aria-valuemin={0}
      aria-valuemax={2}
      aria-valuenow={value || 0}
      aria-valuetext={current.label}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onKeyDown}
    >
      <span className="priority-switch-track">
        {PRIORITIES.map((p) => (
          <OverlayTrigger
            key={p.value}
            placement="top"
            transition={false}
            overlay={<Tooltip id={`tooltip-priority-${taskId}-${p.value}`}>{p.label} : {p.title}{p.value === value ? " (position actuelle)" : ""}</Tooltip>}
          >
            <span className="priority-switch-stop" onClick={() => choose(p.value)} />
          </OverlayTrigger>
        ))}
        <span className="priority-switch-knob" />
      </span>
    </span>
  );
};

PriorityToggle.propTypes = {
  taskId: PropTypes.number.isRequired,
  value: PropTypes.number,
  canOrder: PropTypes.bool,
  busy: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const WaitingProductsRow = ({ task, canOrder, enforced, busy, onSetPriority }) => {
  const order = task.linked_order;
  const { getFormattedContent } = useNumberOfBags(order);

  // Mêmes classes que les lignes de préparation du flow : couleurs VIP / très mécontent
  let classes = "WaitingProductsRow PackingRow Row " + order.status;
  if (task.client.is_vip) classes += " vip_warning";
  else if (order.is_super_venere) classes += " venere_warning";

  return (
    <tr className={classes}>
      <td title={task.id}>
        <TaskName task={task} />
        {enforced ? (
          <PriorityToggle
            taskId={task.id}
            value={task.waiting_priority || 0}
            canOrder={canOrder}
            busy={busy}
            onChange={onSetPriority}
          />
        ) : null}
        {task.nothing_to_search && (
          <WaitingReasonIcon
            id={task.id}
            reason="nothing_to_search"
            title={"Plus rien à chercher : il ne manque que des produits ajoutés à l’expédition. Inutile de la classer."}
          />
        )}
      </td>
      <td className="text-center">{getFormattedContent()}</td>
      <td className="text-right">{order.total_weight}</td>
      <td>
        <span className="transit_area" style={order.transit_area ? {} : { fontStyle: "italic" }}>
          {order.transit_area ? order.transit_area : "(vide)"}
        </span>
      </td>
      <td>
        <a target="_blank" rel="noopener noreferrer" href={process.env.REACT_APP_DELEEVADMIN + "/users/" + task.client.id} title={`user_id: ${task.client.id}`}>
          {task.client.name}
        </a>
      </td>
      <td>{task.address_label}</td>
      <td><MissingTaker task={task} /></td>
      <td className="slots text-nowrap">{slotLabel(order)}</td>
    </tr>
  );
};

WaitingProductsRow.propTypes = {
  task: PropTypes.object.isRequired,
  canOrder: PropTypes.bool,
  enforced: PropTypes.bool,
  busy: PropTypes.bool,
  onSetPriority: PropTypes.func.isRequired,
};

export default WaitingProductsRow;
